import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUiStore, type Language } from '../../stores/ui.store';

function detectBrowserLanguage(): Language {
  const nav = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
  return nav.startsWith('en') ? 'en' : 'es';
}

/**
 * Language control for the unauthenticated funnel (login / registro / no-access).
 * On first visit (no explicit choice yet) it seeds the language from the browser;
 * once the user toggles, that choice wins and persists. The app's global i18n
 * detector stays disabled — this only affects these public pages.
 */
export function useFunnelLanguage() {
  const { i18n } = useTranslation();
  const language = useUiStore((s) => s.language);
  const languageExplicit = useUiStore((s) => s.languageExplicit);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const setDetectedLanguage = useUiStore((s) => s.setDetectedLanguage);

  // On mount: browser-detect unless the user already chose explicitly.
  useEffect(() => {
    const target = languageExplicit ? language : detectBrowserLanguage();
    if (!languageExplicit && target !== language) setDetectedLanguage(target);
    if (i18n.language !== target) void i18n.changeLanguage(target);
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep i18n in sync if the language changes afterwards (e.g. user toggles).
  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language);
  }, [i18n, language]);

  function switchLanguage(lang: Language) {
    setLanguage(lang);
    void i18n.changeLanguage(lang);
  }

  return { language, switchLanguage };
}
